import React from 'react';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Table from 'Components/DesignSystem/Table/Table';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { useConfigurationContext } from './Context/ConfigurationContext';
import { DropdownOption } from './types';
import { TableColumn } from 'types/DesignSystem/Table';

const FEED_TYPE_LABELS: Record<string, string> = {
    products: 'Products',
    orders: 'Orders',
    availability: 'Availability',
    relatedproducts: 'Related Products',
    related_products: 'Related Products',
    price: 'Price',
    prices: 'Price',
    substitutes: 'Substitutes',
    invoice: 'Invoice',
};

type FeedVersionsTableProps = {
    id?: string;
};

type FeedRow = { feed_type: string; version?: string | null };

const toOptions = (versions: unknown): DropdownOption[] => {
    if (!Array.isArray(versions)) return [];
    return versions.map((v) => ({ label: String(v).toUpperCase(), value: String(v) }));
};

/**
 * Configuration → Network Setup → Feed Configurations: which feed runs at which
 * version.
 *
 * On DS `Table` since § 133. The **`accessories` filter and the `FEED_ORDER` sort are
 * unchanged** — `accessories` is deprecated in `midway_connector_svc` and must never be
 * offered (`okf/connector/e2e.md` § 7), and the order is the product's, not
 * alphabetical.
 *
 * It was a `tw-grid tw-grid-cols-2` with hand-drawn borders: `#D0D5DD` for the rules,
 * `#F9FAFB` for the header band and `#344054` for the text — three hardcoded hexes,
 * none of them in this app's palette (they are stock Tailwind UI greys). A two-column
 * grid pretending to be a table is also not a table to a screen reader: no `role`, no
 * column association, so a version dropdown announced with no indication of which feed
 * it belonged to.
 */
export const FeedVersionsTable = ({ id = 'feed-versions-table' }: FeedVersionsTableProps) => {
    const { currentConfig, isEditMode, updateFeedConfiguration } = useConfigurationContext();
    const feedConfigurations = currentConfig?.feed_configurations;
    const connectorVersions = currentConfig?.connector_versions ?? {};

    if (!feedConfigurations || feedConfigurations.length === 0) return null;

    const FEED_ORDER: string[] = ['products', 'orders', 'prices', 'price', 'availability', 'relatedproducts', 'related_products', 'substitutes'];
    const sorted = [...feedConfigurations].filter((f) => f.feed_type.toLowerCase() !== 'accessories').sort((a, b) => {
        const ia = FEED_ORDER.indexOf(a.feed_type.toLowerCase());
        const ib = FEED_ORDER.indexOf(b.feed_type.toLowerCase());
        const orderA = ia === -1 ? FEED_ORDER.length : ia;
        const orderB = ib === -1 ? FEED_ORDER.length : ib;
        return orderA - orderB;
    });

    const columns: TableColumn<FeedRow>[] = [
        {
            id: 'feed_type',
            header: 'Feed type',
            accessor: 'feed_type',
            width: '50%',
            render: (_value, row) => (
                <Typography variant="body-primary" as="span">
                    {FEED_TYPE_LABELS[row.feed_type.toLowerCase()] ?? row.feed_type}
                </Typography>
            ),
        },
        {
            id: 'version',
            header: 'Version',
            accessor: 'version',
            width: '50%',
            cellType: 'dropdown',
            render: (_value, row) => (
                <Dropdown
                    /* § 323 — fills the cell. `TableCell` wraps every cell's children in a
                       flex row, so this control's root is a flex item and defaults to
                       shrink-to-fit; the trigger's own `tw-w-full` then resolves to the
                       width of the text inside it ("V3.0.0", ~155px) rather than to the
                       column. The portalled menu is locked to the trigger's width
                       (`matchWidth`), so the long version labels wrapped and clipped. */
                    className="tw-w-full"
                    label=""
                    aria-label={`Version for ${FEED_TYPE_LABELS[row.feed_type.toLowerCase()] ?? row.feed_type}`}
                    value={row.version || ''}
                    onChange={(val) => updateFeedConfiguration(row.feed_type, String(val) || null)}
                    placeholder="Select version"
                    options={toOptions(connectorVersions[row.feed_type.toLowerCase()] ?? [])}
                    disabled={!isEditMode}
                    usePortal
                    size="sm"
                    data-testid={`${id}-version-${row.feed_type}`}
                />
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            data={sorted}
            rowKey="feed_type"
            headerVariant="light"
            bordered
            data-testid={id}
        />
    );
};
